import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail === "object" && detail.message) return detail.message;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F2EC] px-4">
        <div className="brutal-card p-8 max-w-md w-full" data-testid="reset-bad-token">
          <h1 className="font-heading text-2xl font-black uppercase">Invalid link</h1>
          <p className="font-mono text-sm text-[#4A4A4A] mt-2">
            This reset link is missing its token. Request a new one from the sign-in page.
          </p>
          <button onClick={() => navigate("/")} className="brutal-btn mt-4 w-full">Back to sign in</button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password/reset", { token, password: pw });
      if (data?.session_token) localStorage.setItem("session_token", data.session_token);
      try { localStorage.setItem("vv_user", JSON.stringify(data.user)); } catch (_) { /* ignore */ }
      setUser(data.user);
      toast.success("Password updated. You're signed in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not reset password."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F2EC] dot-grid px-4 py-10">
      <div className="brutal-card p-8 max-w-md w-full corner-marks snap-in" data-testid="reset-card">
        <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Password Reset</div>
        <h1 className="font-heading text-3xl font-black uppercase mt-2">Set a new password</h1>
        <form onSubmit={submit} className="space-y-3 mt-6">
          <div>
            <label className="brutal-label">New password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
              <input className="brutal-input !pl-9" type="password" placeholder="At least 8 characters"
                value={pw} onChange={(e)=>setPw(e.target.value)} minLength={8}
                data-testid="reset-pw-input" required />
            </div>
          </div>
          <div>
            <label className="brutal-label">Confirm</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
              <input className="brutal-input !pl-9" type="password" placeholder="Repeat password"
                value={pw2} onChange={(e)=>setPw2(e.target.value)} minLength={8}
                data-testid="reset-pw2-input" required />
            </div>
          </div>
          <button type="submit" disabled={busy} className="brutal-btn w-full !py-3" data-testid="reset-submit-btn">
            {busy ? "Updating…" : (<>Update password <ArrowRight size={14} className="inline ml-2"/></>)}
          </button>
        </form>
      </div>
    </div>
  );
}
